import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { REGIONS } from 'v2/configs/constants'
import { apiConsoleService } from 'v2/shared/services/api-console-service'
import { settingsService } from 'v2/shared/services/settings-service'
import { getAPIConsoleQueryKeys, getSettingsQueryKeys } from 'v2/shared/utils/query-key-management'
import { getLocalAuth } from 'v2/utils'
import type { RSenderIdListResponse, RSenderIdRegisterPayload } from './sender-id-types'

/**
 * Sender IDs only work for non-US destinations, so the registration flow is shown
 * only to US-region accounts whose SMS geo permissions allow at least one non-US
 * country (SER-5918). IN-region accounts never see it.
 *
 * Returns `undefined` while the geo-permissions response is loading so callers can
 * hide the entry point without hard-redirecting a user who may turn out eligible.
 */
export const useSenderIdAccessQuery = (): { hasSenderIdAccess: boolean | undefined } => {
  const isUSRegion = getLocalAuth().region === REGIONS.US
  const { data, isError } = useQuery({
    queryKey: getAPIConsoleQueryKeys().smsGeoPermissionsBase(undefined),
    queryFn: async () => {
      const response = await apiConsoleService.listSmsGeoPermissions()
      return response.data
    },
    enabled: isUSRegion,
    // staleTime dedupes the three consumers (routes meta, tab, page); focus refetch
    // respects it, so external geo-permission changes surface within ~5 min of a tab focus.
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  if (!isUSRegion || isError) {
    return { hasSenderIdAccess: false }
  }
  if (!data) {
    return { hasSenderIdAccess: undefined }
  }
  return {
    hasSenderIdAccess:
      data.geo_permissions?.some(
        (permission) => permission.sms_enabled && permission.country_code !== 'US',
      ) ?? false,
  }
}

export const useSenderIdCountryOptionsQuery = () =>
  useQuery({
    queryKey: getSettingsQueryKeys().senderIdCountryOptions,
    queryFn: async () => {
      const response = await settingsService.getSenderIdCountryOptions()
      return response.data
    },
  })

export const useRegisterSenderIdMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: RSenderIdRegisterPayload) => settingsService.registerSenderId(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().senderIdsV2Base })
    },
  })
}

export const useSetDefaultSenderIdMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ senderId, isDefault }: { senderId: string; isDefault: boolean }) =>
      settingsService.setDefaultSenderId(senderId, isDefault),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().senderIdsV2Base })
    },
  })
}

export const useSenderIdExistsQuery = () =>
  useQuery({
    queryKey: [...getSettingsQueryKeys().senderIdsV2Base, 'exists'],
    queryFn: async () => {
      const response = await settingsService.listSenderIdsV2({ page: 1, page_size: 1 })
      return response.data.meta.total_count > 0
    },
  })

export const senderIdListConfig = () => ({
  queryKey: getSettingsQueryKeys().senderIdsV2Base,
  fetchService: (queryParams: Record<string, unknown>) =>
    settingsService.listSenderIdsV2(queryParams),
  extractData: (data: RSenderIdListResponse) => ({
    data: data.results,
    totalCount: data.meta.total_count,
  }),
})
